import { SurveyFormFace } from "helpers/enum";
import { getFontFamily } from "./brandingFonts";

/**
 * Single source of truth for branding settings.
 *
 * Values are stored as flat, sparse "layers" keyed by setting key:
 *   { shared: {...}, emailsLandingPages: {...}, surveysForms: {...} }
 * `shared` is the company-wide Brand; each tab can customise on top of it. A value resolves in this order:
 *   tab layer -> shared (Brand) layer -> derived from the brand (`from`) -> built-in `fallback` -> (unset)
 * Unset values are not sent to the editor, so the editor keeps its own default.
 */

export const SCOPES = {
  SHARED: "shared", // the Brand tab
  EMAILS_LANDING_PAGES: "emailsLandingPages", // also drives the email footer and the Profiles & Jobs cards
  SURVEYS_FORMS: "surveysForms",
};

// Brand-level settings and their built-in values (mirror today's editor look).
export const BRAND_DEFAULTS = {
  "colors.primary": "#ee4578",
  "colors.secondary": "#404042",
  "colors.accent": "#ee4578",
  "colors.text": "#000000",
  "colors.background": "#ffffff",
  "colors.extra": [],
  "font.body": "Open Sans",
  "font.heading": "Open Sans",
  "font.size": 14,
  "font.lineHeight": 150,
  "style.buttonRadius": "",
};

export const MAX_EXTRA_COLORS = 6;

export const readableOn = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return "#ffffff";
  const n = parseInt(m[1], 16);
  const luminance = (0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return luminance > 0.6 ? "#000000" : "#ffffff";
};

const OPTIONS = {
  radius: [
    { value: "", label: "Editor default" },
    { value: "0px", label: "Square" },
    { value: "6px", label: "Rounded" },
    { value: "50px", label: "Pill" },
  ],
  fieldRadius: [
    { value: "", label: "Editor default" },
    { value: "0px", label: "Square" },
    { value: "4px", label: "Slightly rounded" },
    { value: "10px", label: "Rounded" },
  ],
  weight: [
    { value: "", label: "Editor default" },
    { value: "400", label: "Regular" },
    { value: "600", label: "Semi-bold" },
    { value: "700", label: "Bold" },
  ],
  lineStyle: [
    { value: "solid", label: "Solid" },
    { value: "dashed", label: "Dashed" },
    { value: "dotted", label: "Dotted" },
  ],
};

// Brand fields shown at the top of the page.
export const BRAND_COLOR_FIELDS = [
  { key: "colors.primary", label: "Primary", hint: "Buttons, links", type: "color" },
  { key: "colors.secondary", label: "Secondary", hint: "Hover, dividers", type: "color" },
  { key: "colors.accent", label: "Accent", hint: "Tables, forms", type: "color" },
  { key: "colors.text", label: "Text", hint: "Body, headings", type: "color" },
  { key: "colors.background", label: "Background", hint: "Content area", type: "color" },
];

// `editorOnly` fields apply to emails and landing pages only; surveys and forms ignore them.
export const BRAND_TEXT_FIELDS = [
  { key: "font.body", label: "Font", hint: "For emails, pages and surveys", type: "font" },
  // Follows the main font unless a different one is chosen, so one font really is one font.
  { key: "font.heading", label: "Heading font", hint: "Follows your font unless you choose another", type: "font", editorOnly: true, from: g => g("font.body") },
  { key: "font.size", label: "Base size", type: "number", unit: "px", min: 10, max: 24 },
  { key: "font.lineHeight", label: "Line height", type: "number", unit: "%", min: 100, max: 200, step: 10, editorOnly: true },
];

export const BRAND_STYLE_FIELDS = [
  { key: "style.buttonRadius", label: "Button shape", type: "select", options: OPTIONS.radius },
];

// ---- helpers used by the block builders -------------------------------------------------

const px = (v) => (v === undefined || v === "" ? undefined : `${v}px`);
const pct = (v) => (v === undefined || v === "" ? undefined : `${v}%`);
const font = (v) => (v ? getFontFamily(v) : undefined);

const field = (block, key, label, type, extra = {}) => ({
  key: `${block}.${key}`,
  local: key,
  label,
  type,
  ...extra,
});

const color = (block, key, label, from) => field(block, key, label, "color", { from });
const fontField = (block, key, label, from) => field(block, key, label, "font", { from });
const size = (block, key, label, from, min = 8, max = 72) =>
  field(block, key, label, "number", { unit: "px", min, max, from });

// Text-like blocks (text, paragraph, list) share the same fields and mapping.
const textLikeBlock = (id, label, description) => ({
  id,
  label,
  description,
  fields: [
    color(id, "color", "Text color", g => g("colors.text")),
    color(id, "linkColor", "Link color", g => g("colors.primary")),
    fontField(id, "fontFamily", "Font", g => g("font.body")),
    size(id, "fontSize", "Font size", g => g("font.size"), 8, 48),
    field(id, "lineHeight", "Line height", "number", { unit: "%", min: 100, max: 250, step: 10, from: g => g("font.lineHeight") }),
  ],
  build: v => ({
    contentDefaults: {
      [id]: {
        styles: {
          color: v.color,
          linkColor: v.linkColor,
          fontFamily: font(v.fontFamily),
          fontSize: px(v.fontSize),
          lineHeight: pct(v.lineHeight),
        },
      },
    },
  }),
});

// ---- block definitions ------------------------------------------------------------------

// Headings with a configurable size, and the largest size allowed for each.
const TITLE_SIZED_LEVELS = [{ level: 1, max: 96 }, { level: 2, max: 72 }, { level: 3, max: 60 }];

export const BLOCKS = [
  {
    id: "general",
    label: "Page",
    description: "Applies to the whole template.",
    fields: [
      color("general", "backgroundColor", "Page background", () => undefined),
      color("general", "contentAreaBackgroundColor", "Content background", g => g("colors.background")),
      color("general", "linkColor", "Link color", g => g("colors.primary")),
      fontField("general", "defaultFont", "Default font", g => g("font.body")),
      field("general", "contentAreaWidth", "Content width", "number", { unit: "px", min: 320, max: 1920, step: 10 }),
    ],
    build: v => ({
      contentDefaults: {
        general: {
          backgroundColor: v.backgroundColor,
          contentAreaBackgroundColor: v.contentAreaBackgroundColor,
          linkColor: v.linkColor,
          defaultFont: font(v.defaultFont),
          contentAreaWidth: px(v.contentAreaWidth),
        },
      },
    }),
  },
  {
    id: "title",
    label: "Title",
    description: "Headings H1 to H6.",
    fields: [
      color("title", "color", "Text color", g => g("colors.text")),
      color("title", "linkColor", "Link color", g => g("colors.primary")),
      fontField("title", "fontFamily", "Font", g => g("font.heading")),
      field("title", "fontWeight", "Weight", "select", { options: OPTIONS.weight }),
      ...TITLE_SIZED_LEVELS.map(({ level, max }) => size("title", `h${level}Size`, `H${level} size`, () => undefined, 12, max)),
    ],
    build: v => ({
      titleDefaultStyles: Object.fromEntries([1, 2, 3, 4, 5, 6].map(level => [`h${level}`, {
        color: v.color,
        "link-color": v.linkColor,
        "font-family": font(v.fontFamily),
        "font-weight": v.fontWeight,
        "font-size": px(v[`h${level}Size`]),
      }])),
    }),
  },
  textLikeBlock("text", "Text", "The default text block."),
  textLikeBlock("paragraph", "Paragraph", "Paragraph blocks."),
  textLikeBlock("list", "List", "Bulleted and numbered lists."),
  {
    id: "button",
    label: "Button",
    description: "Buttons, including their hover state.",
    fields: [
      color("button", "backgroundColor", "Background", g => g("colors.primary")),
      color("button", "color", "Label color", g => readableOn(g("button.backgroundColor"))),
      color("button", "hoverBackgroundColor", "Hover background", g => g("colors.secondary")),
      color("button", "hoverColor", "Hover label color", g => readableOn(g("button.hoverBackgroundColor"))),
      fontField("button", "fontFamily", "Font", g => g("font.body")),
      size("button", "fontSize", "Font size", () => undefined, 8, 48),
      field("button", "fontWeight", "Weight", "select", { options: OPTIONS.weight }),
      field("button", "borderRadius", "Shape", "select", { options: OPTIONS.radius, from: g => g("style.buttonRadius") }),
    ],
    build: v => ({
      contentDefaults: {
        button: {
          styles: {
            backgroundColor: v.backgroundColor,
            color: v.color,
            fontFamily: font(v.fontFamily),
            fontSize: px(v.fontSize),
            fontWeight: v.fontWeight,
            borderRadius: v.borderRadius,
          },
          hoverStyles: {
            backgroundColor: v.hoverBackgroundColor,
            color: v.hoverColor,
          },
        },
      },
    }),
  },
  {
    id: "divider",
    label: "Divider",
    description: "Horizontal lines.",
    fields: [
      color("divider", "color", "Line color", g => g("colors.secondary")),
      size("divider", "thickness", "Thickness", () => undefined, 1, 20),
      field("divider", "lineStyle", "Style", "select", { options: OPTIONS.lineStyle }),
    ],
    build: v => {
      if (v.color === undefined && v.thickness === undefined && v.lineStyle === undefined) return {};
      return {
        contentDefaults: {
          divider: {
            line: `${v.thickness ?? 1}px ${v.lineStyle || "solid"} ${v.color || "#BBBBBB"}`,
          },
        },
      };
    },
  },
  {
    id: "table",
    label: "Table",
    description: "Tables, including the header row.",
    fields: [
      color("table", "color", "Text color", g => g("colors.text")),
      color("table", "linkColor", "Link color", g => g("colors.primary")),
      fontField("table", "fontFamily", "Font", g => g("font.body")),
      size("table", "fontSize", "Font size", g => g("font.size"), 8, 48),
      color("table", "headersBackgroundColor", "Header background", g => g("colors.accent")),
      color("table", "headersColor", "Header text color", g => readableOn(g("table.headersBackgroundColor"))),
      color("table", "alternateRowBackgroundColor", "Alternate row background", () => undefined),
      color("table", "backgroundColor", "Background", () => undefined),
    ],
    build: v => ({
      contentDefaults: {
        table: {
          styles: {
            color: v.color,
            linkColor: v.linkColor,
            fontFamily: font(v.fontFamily),
            fontSize: px(v.fontSize),
            headersBackgroundColor: v.headersBackgroundColor,
            headersColor: v.headersColor,
            alternateRowBackgroundColor: v.alternateRowBackgroundColor,
            backgroundColor: v.backgroundColor,
          },
        },
      },
    }),
  },
  {
    id: "form",
    label: "Form",
    description: "Form blocks: labels, fields and the submit button.",
    fields: [
      fontField("form", "fontFamily", "Font", g => g("font.body")),
      color("form", "labelColor", "Label color", g => g("colors.text")),
      color("form", "fieldColor", "Field text color", g => g("colors.text")),
      color("form", "fieldBackgroundColor", "Field background", g => g("colors.background")),
      color("form", "fieldOutlineColor", "Field outline", g => g("colors.accent")),
      field("form", "fieldBorderRadius", "Field shape", "select", { options: OPTIONS.fieldRadius }),
      color("form", "buttonBackgroundColor", "Button background", g => g("colors.primary")),
      color("form", "buttonColor", "Button label color", g => readableOn(g("form.buttonBackgroundColor"))),
      field("form", "buttonBorderRadius", "Button shape", "select", { options: OPTIONS.radius, from: g => g("style.buttonRadius") }),
    ],
    build: v => ({
      contentDefaults: {
        form: {
          styles: { fontFamily: font(v.fontFamily) },
          labelsOptions: { color: v.labelColor },
          fieldsOptions: {
            color: v.fieldColor,
            backgroundColor: v.fieldBackgroundColor,
            outlineColor: v.fieldOutlineColor,
            borderRadius: v.fieldBorderRadius,
          },
          buttonsOptions: {
            backgroundColor: v.buttonBackgroundColor,
            color: v.buttonColor,
            borderRadius: v.buttonBorderRadius,
          },
        },
      },
    }),
  },
  {
    id: "menu",
    label: "Menu",
    description: "Navigation menus.",
    fields: [
      color("menu", "color", "Text color", g => g("colors.text")),
      color("menu", "linkColor", "Link color", g => g("colors.primary")),
      fontField("menu", "fontFamily", "Font", g => g("font.body")),
      size("menu", "fontSize", "Font size", () => undefined, 8, 48),
    ],
    build: v => ({
      contentDefaults: {
        menu: {
          styles: {
            color: v.color,
            linkColor: v.linkColor,
            fontFamily: font(v.fontFamily),
            fontSize: px(v.fontSize),
          },
        },
      },
    }),
  },
  {
    id: "icons",
    label: "Icons",
    description: "Icon blocks with text.",
    fields: [
      color("icons", "color", "Text color", g => g("colors.text")),
      fontField("icons", "fontFamily", "Font", g => g("font.body")),
      size("icons", "fontSize", "Font size", () => undefined, 8, 48),
    ],
    build: v => ({
      contentDefaults: {
        icons: {
          styles: {
            color: v.color,
            fontFamily: font(v.fontFamily),
            fontSize: px(v.fontSize),
          },
        },
      },
    }),
  },
];

// ---- Surveys, chats and forms -----------------------------------------------------------

const A = SurveyFormFace.Autoscroll;
const C = SurveyFormFace.Classic;
const H = SurveyFormFace.Chat;
const EVERY_RUNNER = [A, C, H];

export const RUNNER_LABELS = { [A]: "Autoscroll", [C]: "Classic", [H]: "Chat" };

const ttField = (section, key, label, type, path, runners, extra = {}) => ({
  key: `tt.${section}.${key}`,
  local: key,
  label,
  type,
  path,
  runners,
  ...extra,
});

const ttColor = (section, key, label, path, runners, from, extra) =>
  ttField(section, key, label, "color", path, runners, { from, ...extra });
const ttNumber = (section, key, label, path, runners, min, max, unit, extra = {}) =>
  ttField(section, key, label, "number", path, runners, { min, max, unit, ...extra });
const ttSelect = (section, key, label, path, runners, options, extra = {}) =>
  ttField(section, key, label, "select", path, runners, { options, ...extra });
// Matches the server's branding string limit.
export const MAX_IMAGE_URL_LENGTH = 2048;

const ttText = (section, key, label, path, runners, extra) =>
  ttField(section, key, label, "text", path, runners, { maxLength: MAX_IMAGE_URL_LENGTH, ...extra });

const buttonRoundness = g => ({ "0px": 0, "6px": 8, "50px": 50 }[g("style.buttonRadius")] ?? 8);
const inputRoundness = g => (g("style.buttonRadius") === "0px" ? 0 : 8);

const BACKGROUND_POSITIONING = [
  { value: "auto", label: "Center" },
  { value: "repeat", label: "Repeat" },
  { value: "cover", label: "Cover (full screen)" },
  { value: "contain", label: "Contain" },
  { value: "100% auto", label: "100% width" },
  { value: "auto 100%", label: "100% height" },
];
const ALIGNMENT = [{ value: "left", label: "Left" }, { value: "right", label: "Right" }];
const BUTTON_MODE = [{ value: "fill", label: "Fill" }, { value: "outline", label: "Outline" }];

export const TRIPETTO_SECTIONS = [
  {
    id: "text",
    label: "Text",
    description: "Font and text color.",
    fields: [
      ttColor("text", "color", "Text color", "color", EVERY_RUNNER, g => g("colors.text")),
      ttField("text", "fontFamily", "Font", "font", "font.family", EVERY_RUNNER, { from: g => g("font.body") }),
      ttNumber("text", "fontSize", "Font size", "font.size", EVERY_RUNNER, 8, 30, "px", { from: g => g("font.size") }),
      ttNumber("text", "fontSizeSmall", "Font size (small screens)", "font.sizeSmall", EVERY_RUNNER, 8, 30, "px"),
    ],
  },
  {
    id: "background",
    label: "Background",
    description: "Page color and image.",
    fields: [
      ttColor("background", "color", "Color", "background.color", EVERY_RUNNER, g => g("colors.background")),
      ttText("background", "url", "Image URL", "background.url", EVERY_RUNNER),
      ttNumber("background", "opacity", "Image opacity", "background.opacity", EVERY_RUNNER, 0, 100, "%"),
      ttSelect("background", "positioning", "Image positioning", "background.positioning", EVERY_RUNNER, BACKGROUND_POSITIONING),
    ],
  },
  {
    id: "inputs",
    label: "Inputs",
    description: "Text fields, choices and validation colors.",
    fields: [
      ttColor("inputs", "backgroundColor", "Background", "inputs.backgroundColor", EVERY_RUNNER, g => g("colors.background")),
      ttColor("inputs", "borderColor", "Border color", "inputs.borderColor", EVERY_RUNNER),
      ttNumber("inputs", "borderSize", "Border size", "inputs.borderSize", EVERY_RUNNER, 1, 20, "px", { fallback: 1 }),
      ttNumber("inputs", "roundness", "Roundness", "inputs.roundness", EVERY_RUNNER, 0, 50, "px", { from: inputRoundness }),
      ttColor("inputs", "textColor", "Text color", "inputs.textColor", EVERY_RUNNER, g => g("colors.text")),
      ttColor("inputs", "selectionColor", "Selection color", "inputs.selectionColor", EVERY_RUNNER, g => g("colors.accent")),
      ttColor("inputs", "agreeColor", "Agree color", "inputs.agreeColor", EVERY_RUNNER, g => g("colors.primary")),
      ttColor("inputs", "declineColor", "Decline color", "inputs.declineColor", EVERY_RUNNER, g => g("colors.secondary")),
      ttColor("inputs", "errorColor", "Error color", "inputs.errorColor", EVERY_RUNNER, undefined, { fallback: "#ff0000" }),
    ],
  },
  {
    id: "buttons",
    label: "Buttons",
    description: "Next and submit buttons.",
    fields: [
      ttColor("buttons", "baseColor", "Button color", "buttons.baseColor", EVERY_RUNNER, g => g("colors.secondary")),
      ttColor("buttons", "finishColor", "Submit button color", "buttons.finishColor", [A, C], g => g("colors.primary")),
      ttColor("buttons", "textColor", "Label color", "buttons.textColor", EVERY_RUNNER),
      ttSelect("buttons", "mode", "Style", "buttons.mode", EVERY_RUNNER, BUTTON_MODE, { fallback: "fill" }),
      ttNumber("buttons", "roundness", "Roundness", "buttons.roundness", EVERY_RUNNER, 0, 50, "px", { from: buttonRoundness }),
    ],
  },
  {
    id: "navigation",
    label: "Navigation bar",
    description: "The bar with the progress and back/next controls.",
    fields: [
      ttColor("navigation", "backgroundColor", "Background", "navigation.backgroundColor", [A]),
      ttColor("navigation", "textColor", "Text color", "navigation.textColor", [A]),
      ttColor("navigation", "progressbarColor", "Progressbar color", "navigation.progressbarColor", [A], g => g("colors.primary")),
    ],
  },
  {
    id: "chat",
    label: "Chat bubbles",
    description: "Questions, answers, avatar and the chat button.",
    fields: [
      ttSelect("chat", "questionsAlignment", "Question alignment", "questions.alignment", [H], ALIGNMENT, { fallback: "left" }),
      ttNumber("chat", "questionsRoundness", "Question roundness", "questions.roundness", [H], 0, 50, "px"),
      ttColor("chat", "questionsBackgroundColor", "Question background", "questions.backgroundColor", [H]),
      ttColor("chat", "questionsBorderColor", "Question border", "questions.borderColor", [H]),
      ttColor("chat", "questionsTextColor", "Question text", "questions.textColor", [H], g => g("colors.text")),
      ttSelect("chat", "answersAlignment", "Answer alignment", "answers.alignment", [H], ALIGNMENT, { fallback: "right" }),
      ttNumber("chat", "answersRoundness", "Answer roundness", "answers.roundness", [H], 0, 50, "px"),
      ttColor("chat", "answersBackgroundColor", "Answer background", "answers.backgroundColor", [H], g => g("colors.primary")),
      ttColor("chat", "answersBorderColor", "Answer border", "answers.borderColor", [H]),
      ttColor("chat", "answersTextColor", "Answer text", "answers.textColor", [H], g => readableOn(g("tt.chat.answersBackgroundColor"))),
      ttText("chat", "avatarImage", "Avatar image URL", "avatar.image", [H]),
      ttColor("chat", "chatButtonColor", "Chat button color", "chatButton.color", [H], g => g("colors.primary")),
      ttText("chat", "chatButtonImage", "Chat button icon URL", "chatButton.image", [H]),
    ],
  },
];

// Every setting, brand-level and block-level, by key: the brand ones can derive from each other too (`from`).
export const FIELDS_BY_KEY = [
  { fields: [...BRAND_COLOR_FIELDS, ...BRAND_TEXT_FIELDS, ...BRAND_STYLE_FIELDS] },
  ...BLOCKS,
  ...TRIPETTO_SECTIONS,
].reduce((map, block) => {
  block.fields.forEach(f => { map[f.key] = f; });
  return map;
}, {});

// ---- resolution -------------------------------------------------------------------------

export const EMPTY_LAYERS = { shared: {}, emailsLandingPages: {}, surveysForms: {} };
export const LAYER_NAMES = Object.keys(EMPTY_LAYERS);

const has = (layer, key) => layer && layer[key] !== undefined;

/** Effective value of a setting for a scope (a tab), as shown on the Branding page (built-in defaults included). */
export const resolveValue = (layers, target, key) => {
  if (target !== SCOPES.SHARED && has(layers[target], key)) return layers[target][key];
  if (has(layers.shared, key)) return layers.shared[key];
  const def = FIELDS_BY_KEY[key];
  if (def) {
    const derived = def.from ? def.from(k => resolveValue(layers, target, k)) : undefined;
    if (derived !== undefined) return derived;
    if (def.fallback !== undefined) return def.fallback;
  }
  return BRAND_DEFAULTS[key];
};

/**
 * Value of a setting for an editor: only what the company set, or what is derived from settings it set.
 * Built-in defaults and fallbacks are left out (undefined), so every editor keeps its own default for
 * anything the company did not choose. A derived value is dropped if any setting it reads is unset.
 */
export const resolveSetValue = (layers, target, key) => {
  if (target !== SCOPES.SHARED && has(layers[target], key)) return layers[target][key];
  if (has(layers.shared, key)) return layers.shared[key];
  const def = FIELDS_BY_KEY[key];
  if (!def || !def.from) return undefined;
  let missing = false;
  const derived = def.from(k => {
    const v = resolveSetValue(layers, target, k);
    if (v === undefined || v === "") missing = true;
    return v;
  });
  return missing ? undefined : derived;
};

/** Only http(s) URLs are accepted for images (background, avatar, chat button). */
export const isHttpUrl = (value) => /^https?:\/\/[^\s"'()<>]+$/i.test(String(value || "").trim());

/**
 * Where the value shown for `scope` comes from:
 * "custom" (set at this scope), "inherited" (set on the Brand tab), "derived" (from the brand),
 * "default" (a built-in value) or "unset".
 */
export const sourceOf = (layers, scope, key) => {
  if (has(layers[scope], key)) return "custom";
  if (scope !== SCOPES.SHARED && has(layers.shared, key)) return "inherited";
  if (key in BRAND_DEFAULTS) return "default";
  const def = FIELDS_BY_KEY[key];
  if (def && def.from && def.from(k => resolveValue(layers, scope, k)) !== undefined) return "derived";
  if (def && def.fallback !== undefined) return "default";
  return "unset";
};

export const isEmptyLayers = (layers) =>
  Object.values(layers).every(layer => Object.keys(layer).length === 0);
